import React, { useCallback, useEffect, useState } from "react";

import { Box, Flex, useToast } from "@chakra-ui/react";
import { Skeleton, Stack } from "@chakra-ui/react";
import { useFeatureIsOn } from "@growthbook/growthbook-react";

import Typography from "../../components/Common/Typography";
import HelmetWrapper from "../../utils/HelmetWrapper";
import { callApiWithToken } from "../../utils/http_common";
import Currencies from "./Cards/Currencies";
import Integration from "./Cards/Integration";
import IntegrationNew from "./Cards/IntegrationNew";
import { MIDTable } from "./MID";
import Teams from "./teams";

const Settings = () => {
  const [tab, setTab] = useState("integration");
  const [integrationData, setIntegrationData] = useState({});
  const [teamsData, setTeamsData] = useState([]);
  const [joinRequests, setJoinRequests] = useState([]);
  const [loading, setLoading] = useState({});
  const [walletdata, setWalletData] = useState({});
  const enabled = useFeatureIsOn("customer-kms");

  const toast = useToast();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const { data: integrationData } = (await callApiWithToken.post("/organization/details", { impersonatedOrg: "all" })) || {};
      const { data: teamsData } = (await callApiWithToken.get("/customer/team/list")) || {};
      const { customers = [], joinRequests = [] } = teamsData?.data || {};

      setIntegrationData(integrationData);
      setTeamsData(customers);
      setJoinRequests(joinRequests);

      setWalletData(integrationData);
    } catch (err) {
      toast({
        title: "Error fetching data",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <>
      <HelmetWrapper title="API Customer Dashboard - Settings" description="API Customer Dashboard settings page" />
      <Box px={8} py={5} bg={"#F2F2F2"} minH={"100vh"}>
        <Typography color="default" type="largeheader" weight="bold">
          Settings
        </Typography>

        <Flex mt={"40px"} ml={"40px"} gap={6}>
          <Flex
            direction={"column"}
            gap={7}
            borderLeft={"3px solid"}
            borderLeftColor={"#1A4FD6"}
            py={"7px"}
            px={"7px"}
            h={"100px"}
            justify={"center"}
            w={"216px"}
          >
            <Typography
              color="primary"
              colorweight={tab === "integration" ? "500" : "200"}
              type="body"
              weight="bold"
              _hover={{ cursor: "pointer" }}
              onClick={() => setTab("integration")}
            >
              Integration
            </Typography>
            <Typography
              color="primary"
              colorweight={tab === "teams" ? "500" : "200"}
              type="body"
              weight="bold"
              _hover={{ cursor: "pointer" }}
              onClick={() => setTab("teams")}
            >
              Teams
            </Typography>
            <Typography
              color="primary"
              colorweight={tab === "mid" ? "500" : "200"}
              type="body"
              weight="bold"
              _hover={{ cursor: "pointer" }}
              onClick={() => setTab("mid")}
            >
              MID Management
            </Typography>
            {import.meta.env.VITE_NODE_ENV === "development" && (
              <Typography
                color="primary"
                colorweight={tab === "currencies" ? "500" : "200"}
                type="body"
                weight="bold"
                _hover={{ cursor: "pointer" }}
                onClick={() => setTab("currencies")}
              >
                Currencies
              </Typography>
            )}
          </Flex>

          {loading ? (
            <Box bg="white" w="100%" borderRadius={"9px"} p={"20px 40px"}>
              <Stack borderRadius={"9px"} mt={"15px"} rowGap={5} bg={"white"}>
                {Array(6)
                  .fill(0)
                  .map((_, index) => (
                    <Skeleton key={index} height="60px" />
                  ))}
              </Stack>
            </Box>
          ) : (
            <Box px={"10"} bg={"#FFFFFF"} w={"full"} rounded={"9px"} minH={"600px"} border={"1px"} borderColor={"#E4E4E4"}>
              {tab === "integration" ? enabled ? <IntegrationNew data={integrationData} /> : <Integration data={integrationData} /> : null}
              {tab === "teams" ? <Teams team={teamsData} joinRequests={joinRequests} refreshData={fetchData} /> : null}
              {tab === "mid" ? <MIDTable /> : null}
              {tab === "currencies" ? <Currencies data={walletdata} /> : null}
            </Box>
          )}
        </Flex>
      </Box>
    </>
  );
};

export default Settings;
